'use client';

import { useState, useEffect } from 'react';
import type { TocItem } from '@/types/table-of-contents';

interface TableOfContentsProps {
  items: TocItem[];
}

export default function TableOfContents({ items }: TableOfContentsProps) {
  const [activeId, setActiveId] = useState<string>('');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: '0% 0% -80% 0%' },
    );

    // Observe all headings
    items.forEach((item) => {
      const element = document.getElementById(item.slug);
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [items]);

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <nav className="toc-container">
      <div className="bg-white/80 backdrop-blur-sm rounded-lg border border-zinc-200 shadow-sm p-6">
        <h2 className="text-sm font-semibold text-zinc-900 mb-4 uppercase tracking-wider">
          In this article
        </h2>
        <ul className="space-y-2 text-sm">
          {items.map((item) => {
            const isActive = activeId === item.slug;
            const levelClass = item.level === 3 ? 'pl-4' : '';

            return (
              <li key={item.slug} className={levelClass}>
                <a
                  href={`#${item.slug}`}
                  className={`block py-1 transition-colors duration-200 border-l-2 pl-3 -ml-px ${
                    isActive
                      ? 'border-black text-black font-medium'
                      : 'border-transparent text-zinc-600 hover:text-zinc-900 hover:border-zinc-300'
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    const element = document.getElementById(item.slug);
                    if (element) {
                      const offset = 80;
                      const elementPosition = element.getBoundingClientRect().top;
                      const offsetPosition = elementPosition + window.pageYOffset - offset;

                      window.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth',
                      });
                    }
                  }}
                >
                  {item.text}
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}
